import Analytics from '@/utils/analytics';
import ArrowLeft from '@assets/icons/arrow-left.svg';
import ChevronLeft from '@assets/icons/chevron-double-left.svg';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import RefreshIcon from '@assets/icons/refresh-swap.svg';
import {
  CONTAINER_MODAL_ROUTES,
  ORDER_INBOUNDS_ROUTES,
} from '@/navigators/routes';
import { createStyles } from '@/contexts/theme-context';
import {
  FILTER_STATUS_SETS,
  InboundListFilter,
} from './order-inbounds-order-list/types';
import {
  getOrderInboundOptions,
  getOrderIndexInSectionData,
  OrdersByCustomerMap,
  PAGE_CONTAINER_PADDING_HORIZONTAL,
} from './utils';
import { ICON_BUTTON_HIT_SLOP } from '@/components/navigation-header-content';
import { InboundListSplitPageContainer } from './inbound-list-split-page-container';
import { MergeOrderInboundsActionSheet } from './merge-order-inbounds-action-sheet';
import { ORDER_INBOUNDS_THREAD_MESSAGE_RELS_SUBSCRIPTION } from './queries';
import { OrderInboundOption } from './types';
import {
  OrderInbounds_ThreadMessageRelsSubscription,
  OrderInbounds_ThreadMessageRelsSubscriptionVariables,
  SmartOrderInboundHistoryOrderBy,
} from '@/types';
import { OrderInboundsOrderList } from './order-inbounds-order-list/order-inbounds-order-list';
import { OrderInboundsOrderListFilterForm } from './order-inbounds-order-list/order-inbounds-order-list-filter-form';
import { type Props as OrderInboundsOrderListProps } from './order-inbounds-order-list/order-inbounds-order-list';
import { OrderInboundsStackParams } from '@/navigators/types';
import { OrderInboundWrapper } from './order-inbound-wrapper';
import { PAGE_CONTAINER_HEADER_HEIGHT } from '@/styles';
import { PageContainer } from '@/components/page-container';
import { Platform, StatusBar, TouchableOpacity, View } from 'react-native';
import { RecyclerListRef } from '@/components/recycler-list';
import { RemoveOrderInboundActionSheet } from './remove-order-inbound-action-sheet';
import { setNextLayoutAnimation } from '@/utils/layout-animation';
import { SmartOrderStatus } from '@/enums';
import { StackScreenProps } from '@react-navigation/stack';
import { TrainingModeSwitch } from '@/components/drawer-navigation/training-mode-switch';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOrderInboundListData } from './hooks/use-order-inbound-list-data';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { useOrderInboundsOrderListDrawerContext } from './order-inbounds-order-list-drawer/order-inbounds-order-list-drawer-context';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSubscription } from '@/contexts/subscription-context';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useThreadOrderEventsContext } from './thread-order-events-context';
import { useToasts } from '@/contexts/toast-context';

interface ActiveSelection {
  mergeUUID: string | null;
  order: OrderInboundOption | null;
  rejectedUpdatedAt: string | null;
  submittedOrderUUID: string | null;
  threadIds: string[];
}

type Props = StackScreenProps<
  OrderInboundsStackParams,
  ORDER_INBOUNDS_ROUTES.List
>;

export const OrderInboundsListScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { navigate } = navigation;
  const { initialSmartOrderUUID } = route.params || {};
  const { showErrorToast } = useToasts();
  const [styles, theme] = useStyles();
  const { isSmallLayout, isSmallTabletLayout, isLargeLayout, isMediumLayout } =
    useLayoutContext();
  const isEmailThreadsEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'email_threads_enabled',
  );
  const isInboundHistoryEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'inbound_history_enabled',
  );

  const isSmallMobile =
    (isSmallLayout || isSmallTabletLayout) && Platform.OS !== 'web';
  const initialActiveOrderSet = useRef(false);
  const prevInitialSmartOrderUUID = useRef<string | undefined>(
    initialSmartOrderUUID,
  );
  const prevThreadOrderUUIDsRef = useRef<Map<string, Set<string>>>(new Map());
  const threadOrderUUIDsInitializedRef = useRef(false);

  const [isInboundsListExpanded, setIsInboundsListExpanded] = useState(true);
  const [activeSelection, setActiveSelection] = useState<ActiveSelection>({
    mergeUUID: null,
    order: null,
    rejectedUpdatedAt: null,
    submittedOrderUUID: null,
    threadIds: [],
  });

  useEffect(() => {
    setIsInboundsListExpanded(true);
  }, [activeSelection]);

  // Identity of the selected inbound, used as the OrderInboundWrapper key
  // below. A thread collapses to one value because a reply changes which smart
  // order backs the row, and that should not remount the panel being reviewed.
  const activeInboundKey = useMemo(() => {
    if (!activeSelection.order) {
      return null;
    }

    return activeSelection.threadIds.length > 0
      ? [
          [...activeSelection.threadIds].sort().join(','),
          activeSelection.submittedOrderUUID ?? '',
          activeSelection.rejectedUpdatedAt ?? '',
        ].join('|')
      : String(activeSelection.order.value);
  }, [activeSelection]);

  const [activeCustomerId, setActiveCustomerId] = useState<string | null>(null);
  const [initialMergeOrderUUID, setInitialMergeOrderUUID] = useState<
    string | null
  >(null);
  const [ordersToRemove, setOrdersToRemove] = useState<string[] | null>(null);

  const { top: paddingTop } = useSafeAreaInsets();

  const { isViewOnly, isOrderAgentOnly } = useOrderInbounds();

  const {
    open: drawerOpen,
    openDrawer,
    closeDrawer,
    setDrawerState,
  } = useOrderInboundsOrderListDrawerContext();

  const { emitThreadOrderProcessing } = useThreadOrderEventsContext();

  const handleSetActiveCustomerId = useCallback(
    (customerId: string, orderUUID?: string) => {
      setActiveCustomerId(customerId);
      setInitialMergeOrderUUID(orderUUID ?? null);
    },
    [],
  );

  const onNavigateToAppStack = useCallback(() => {
    if (isOrderAgentOnly) {
      // there may not be anywhere to go back to if order agent only.
      // so directly navigate to the app stack when they hit the back button.
      //
      // additional note: we need to reset the navigation stack here as if we don't want OA
      // to be part of the stack history. the base route assumes that it is the default route, so any calls like
      // popToTop would navigate all the way back to OA, which is behavior we don't want.
      navigation.reset({
        index: 0,
        routes: [{ name: CONTAINER_MODAL_ROUTES.Base }],
      });
    } else {
      navigation.goBack();
    }
  }, [isOrderAgentOnly, navigation]);

  // Keep the order(s) shown in the detail pane subscribed to live updates even
  // if they aren't in any cached list -- e.g. the user opened one from a search
  // and has since narrowed the term past it.
  const selectedOrderUUIDs = useMemo(() => {
    const activeOrder = activeSelection.order;
    if (!activeOrder) {
      return [];
    }
    const mergedUUIDs = activeOrder.metadata?.orderUUIDs;
    return mergedUUIDs?.length ? mergedUUIDs : [String(activeOrder.value)];
  }, [activeSelection.order]);

  const {
    data,
    error,
    fetchNextPage,
    hasLoadFailed,
    isFetchingNextPage,
    isRetryingLoad,
    loading,
    refetchAll,
    retryActiveTab,
    hasResponse,
  } = useOrderInboundListData({ pinnedOrderUUIDs: selectedOrderUUIDs });

  const renderFullWidth = isSmallLayout || isSmallTabletLayout;

  const onBack = useCallback(() => {
    setActiveSelection({
      mergeUUID: null,
      order: null,
      rejectedUpdatedAt: null,
      submittedOrderUUID: null,
      threadIds: [],
    });
    if (renderFullWidth) {
      navigate(CONTAINER_MODAL_ROUTES.OrderInbounds, {
        screen: ORDER_INBOUNDS_ROUTES.List,
      });
    }
  }, [navigate, renderFullWidth]);

  useEffect(() => {
    if (error && !isRetryingLoad) {
      setIsInboundsListExpanded(true);
      showErrorToast();
    }
  }, [error, isRetryingLoad, showErrorToast]);

  const addRemovedOrderUUID = useOrderInboundsStore(
    (store) => store.actions.addRemovedOrderUUID,
  );

  const isSmartOrderSubmitted = useOrderInboundsStore(
    (state) => state.actions.isSmartOrderSubmitted,
  );

  const submittedSmartOrders = useOrderInboundsStore(
    (state) => state.submittedSmartOrders,
  );

  const setActiveStatusFilter = useOrderInboundsStore(
    (store) => store.actions.setActiveStatusFilter,
  );

  const setQueryFilters = useOrderInboundsStore(
    (store) => store.actions.setQueryFilters,
  );

  const activeStatusFilter = useOrderInboundsStore(
    (store) => store.activeStatusFilter,
  );

  const appliedSortBy = useOrderInboundsStore(
    (store) => store.queryFilters.sortBy,
  );

  const inboundListCounts = useOrderInboundsStore(
    (store) => store.inboundListCounts,
  );

  const uniqueThreadIds = useMemo(() => {
    if (!data) {
      return [];
    }
    const ids = new Set<string>();
    for (const order of data.smart_order) {
      if (order.thread_id) {
        ids.add(order.thread_id);
      }
    }
    return Array.from(ids);
  }, [data]);

  const [hasReceivedThreadMessageRels, setHasReceivedThreadMessageRels] =
    useState(false);

  const { data: threadMessageRelsData, loading: isThreadMessageRelsLoading } =
    useSubscription<
      OrderInbounds_ThreadMessageRelsSubscription,
      OrderInbounds_ThreadMessageRelsSubscriptionVariables
    >(ORDER_INBOUNDS_THREAD_MESSAGE_RELS_SUBSCRIPTION, {
      onData: ({ data: subscriptionData }) => {
        setHasReceivedThreadMessageRels(true);
        const rows = subscriptionData.data?.smart_order_message_rel ?? [];
        if (!threadOrderUUIDsInitializedRef.current) {
          for (const row of rows) {
            const set =
              prevThreadOrderUUIDsRef.current.get(row.thread_id) ?? new Set();
            set.add(row.smart_order_uuid);
            prevThreadOrderUUIDsRef.current.set(row.thread_id, set);
          }
          threadOrderUUIDsInitializedRef.current = true;
          return;
        }
        for (const row of rows) {
          const set =
            prevThreadOrderUUIDsRef.current.get(row.thread_id) ?? new Set();
          if (!set.has(row.smart_order_uuid)) {
            set.add(row.smart_order_uuid);
            prevThreadOrderUUIDsRef.current.set(row.thread_id, set);
            emitThreadOrderProcessing({
              smartOrderUUID: row.smart_order_uuid,
              threadId: row.thread_id,
            });
          }
        }
      },
      skip: !isEmailThreadsEnabled || uniqueThreadIds.length === 0,
      variables: { threadIds: uniqueThreadIds },
    });

  const visibleSmartOrderUUIDs = useMemo(() => {
    return new Set((data?.smart_order ?? []).map((o) => o.uuid));
  }, [data]);

  const threadCountByThreadId = useMemo(() => {
    const rows = threadMessageRelsData?.smart_order_message_rel ?? [];
    const counts: Record<string, number> = {};
    for (const row of rows) {
      if (visibleSmartOrderUUIDs.has(row.smart_order_uuid)) {
        counts[row.thread_id] = (counts[row.thread_id] ?? 0) + 1;
      }
    }
    return counts;
  }, [threadMessageRelsData, visibleSmartOrderUUIDs]);

  // Only gate list rendering on this subscription before its very first
  // response. A resubscribe (e.g. triggered when pagination surfaces threads
  // we haven't seen before, changing the `threadIds` variable) should never
  // blank an already-populated list -- keep showing current counts
  // (defaulting brand-new threads to 0) until the resubscribe resolves.
  const isEmailThreadsLoading =
    uniqueThreadIds.length > 0 &&
    isThreadMessageRelsLoading &&
    !hasReceivedThreadMessageRels;

  const { sectionData, ordersByCustomer } = useMemo(() => {
    if (hasLoadFailed) {
      return { ordersByCustomer: {} as OrdersByCustomerMap, sectionData: [] };
    }
    if (loading || isEmailThreadsLoading || !data) {
      return { ordersByCustomer: {} as OrdersByCustomerMap, sectionData: null };
    }
    // Filter out orders whose status no longer belongs to the active status
    // filter (e.g. an order that's since been submitted/updated by other
    // users, or a locally-submitted order that shouldn't linger in the Open
    // view before the server confirms).
    const filteredData = {
      smart_order: data.smart_order.filter(({ uuid, status }) => {
        const statusEnum = status as SmartOrderStatus;
        const isLocallySubmittedButStillShownAsOpen =
          isSmartOrderSubmitted(uuid) &&
          activeStatusFilter === InboundListFilter.Open;
        return (
          !isLocallySubmittedButStillShownAsOpen &&
          FILTER_STATUS_SETS[activeStatusFilter].has(statusEnum)
        );
      }),
    };

    return getOrderInboundOptions(
      filteredData,
      threadCountByThreadId,
      isEmailThreadsEnabled,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    hasLoadFailed,
    isEmailThreadsEnabled,
    loading,
    isEmailThreadsLoading,
    activeStatusFilter,
    isSmartOrderSubmitted,
    // We want the filtered data to operate on the submittedSmartOrders state, so we add this to the dependency array
    // to ensure that this useMemo re-runs whenever the submittedSmartOrders map changes
    submittedSmartOrders,
    threadCountByThreadId,
  ]);

  const selectNextSmartOrder = useCallback(
    (smartOrderUUID: string) => {
      let targetOrder: OrderInboundOption | null = null;

      if (sectionData) {
        for (const section of sectionData) {
          const foundSmartOrderIndex = section.data.findIndex((option) => {
            const allSmartOrdersUUIDs = option.metadata?.orderUUIDs ?? [];
            return allSmartOrdersUUIDs.some(
              (orderUUID) => orderUUID === smartOrderUUID,
            );
          });
          if (foundSmartOrderIndex !== -1) {
            // If there's no order before or after the found index, clear selected order
            // to prevent selecting one we're trying to navigate away from
            targetOrder =
              section.data[foundSmartOrderIndex + 1] ??
              section.data[foundSmartOrderIndex - 1] ??
              null;
            break;
          }
        }
      }
      if (targetOrder) {
        setActiveSelection({
          mergeUUID: targetOrder.metadata?.mergeUUID ?? null,
          order: targetOrder,
          rejectedUpdatedAt: targetOrder.metadata?.rejectedUpdatedAt ?? null,
          submittedOrderUUID: targetOrder.metadata?.submittedOrderUUID ?? null,
          threadIds: targetOrder.metadata?.threadIds ?? [],
        });
      } else {
        setActiveSelection({
          mergeUUID: null,
          order: null,
          rejectedUpdatedAt: null,
          submittedOrderUUID: null,
          threadIds: [],
        });
      }
    },
    [sectionData],
  );

  const selectSmartOrderByIds = useCallback(
    (smartOrderUUIDs: string[], mergeUUID?: string) => {
      let targetOrder: OrderInboundOption | null = null;

      if (sectionData) {
        for (const section of sectionData) {
          const foundSmartOrder = section.data.find((option) => {
            const allSmartOrdersUUIDs = option.metadata?.orderUUIDs ?? [];
            return allSmartOrdersUUIDs.some((orderUUID) =>
              smartOrderUUIDs.includes(orderUUID),
            );
          });
          if (foundSmartOrder) {
            targetOrder = foundSmartOrder;
            break;
          }
        }
      }
      if (targetOrder) {
        setActiveSelection({
          mergeUUID: mergeUUID ?? targetOrder.metadata?.mergeUUID ?? null,
          order: targetOrder,
          rejectedUpdatedAt: targetOrder.metadata?.rejectedUpdatedAt ?? null,
          submittedOrderUUID: targetOrder.metadata?.submittedOrderUUID ?? null,
          threadIds: targetOrder.metadata?.threadIds ?? [],
        });
      }
    },
    [sectionData],
  );

  const scrollData = useRef<{
    orderUUID: string;
    restaurantUUID: string;
  } | null>(null);

  useEffect(() => {
    if (scrollData.current && sectionData && listRef.current) {
      const index = getOrderIndexInSectionData(
        sectionData,
        scrollData.current.orderUUID,
        scrollData.current.restaurantUUID,
      );
      if (index === -1) {
        return;
      }
      scrollData.current = null;
      listRef.current.scrollToIndex({
        animated: true,
        index,
      });
    }
  }, [sectionData]);

  // Ensure active order has not already been submitted.
  // NOTE: Do not use activeOrder as a dependency to avoid this effect causing itself to re-run.
  useEffect(() => {
    setActiveSelection((currentActiveSelection) => {
      // Reset active order if the list is empty
      if (!sectionData || sectionData.length === 0) {
        setIsInboundsListExpanded(true);
        return {
          mergeUUID: null,
          order: null,
          rejectedUpdatedAt: null,
          submittedOrderUUID: null,
          threadIds: [],
        };
      }

      const activeOrder = currentActiveSelection.order;
      if (!activeOrder || !hasResponse) {
        return currentActiveSelection;
      }
      const activeThreadIds = currentActiveSelection.threadIds;
      const orderInList = sectionData.find((section) =>
        section.data.some((order) => {
          if (order.value === activeOrder.value) {
            return true;
          }
          // For email thread orders, a new reply creates a new smart order UUID.
          // Match by thread ID so we don't treat the updated option as "gone".
          if (
            activeThreadIds.length > 0 &&
            order.metadata?.threadIds &&
            order.metadata.threadIds.length > 0
          ) {
            return activeThreadIds.some((id) =>
              order.metadata!.threadIds!.includes(id),
            );
          }
          return false;
        }),
      );

      if (!orderInList || isSmartOrderSubmitted(String(activeOrder.value))) {
        // If the active order has been submitted, then activate the first unsubmitted order
        for (const section of sectionData) {
          for (const order of section.data) {
            if (!isSmartOrderSubmitted(String(order.value))) {
              return {
                mergeUUID: order.metadata?.mergeUUID ?? null,
                order,
                rejectedUpdatedAt: order.metadata?.rejectedUpdatedAt ?? null,
                submittedOrderUUID: order.metadata?.submittedOrderUUID ?? null,
                threadIds: order.metadata?.threadIds ?? [],
              };
            }
          }
        }
      }
      return currentActiveSelection;
    });
  }, [isSmartOrderSubmitted, sectionData, hasResponse]);

  const setOrderInboundsListDrawerOpen = useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        openDrawer();
      } else {
        closeDrawer();
      }
    },
    [openDrawer, closeDrawer],
  );

  const navigateToOrderDetail = useCallback(
    (inboundOption: OrderInboundOption) => {
      navigate(ORDER_INBOUNDS_ROUTES.Detail, {
        initialAssignedEmployee: inboundOption.metadata?.assignedEmployee,
        initialMergeUUID: inboundOption.metadata?.mergeUUID
          ? String(inboundOption.metadata.mergeUUID)
          : undefined,
        initialProcessing: inboundOption.metadata?.isProcessing ?? false,
        initialRejectedUpdatedAt: isEmailThreadsEnabled
          ? inboundOption.metadata?.rejectedUpdatedAt
          : undefined,
        initialRestaurantName: inboundOption.metadata?.restaurantName ?? null,
        initialRestaurantUUID: inboundOption.metadata?.restaurantUUID ?? null,
        initialSubmittedOrderUUID: isEmailThreadsEnabled
          ? inboundOption.metadata?.submittedOrderUUID
          : undefined,
        initialSupplierUUID: inboundOption.metadata?.supplierUUID ?? '',
        initialThreadIds: isEmailThreadsEnabled
          ? inboundOption.metadata?.threadIds
          : undefined,
        onClose: onBack,
        orderUUID: String(inboundOption.value),
        setOrderInboundsListDrawerOpen,
        unknownContactInfo: inboundOption?.metadata?.unknownContactInfo,
      });
    },
    [onBack, navigate, setOrderInboundsListDrawerOpen, isEmailThreadsEnabled],
  );

  const getRemoveHandler =
    (
      option: OrderInboundOption,
      onCloseConfirmationModal: () => void = () => {},
    ) =>
    () => {
      const { openOrderUUIDs } = option.metadata ?? {};
      const smartOrderUUIDs = openOrderUUIDs?.length
        ? openOrderUUIDs
        : [String(option.value)];
      Analytics.track('order_inbounds_list_screen.remove_order_button', {
        smartOrderUUIDs,
      });
      setOrdersToRemove(smartOrderUUIDs);
      onCloseConfirmationModal();
    };

  const listRef = useRef<RecyclerListRef<OrderInboundOption>>(null);

  const OrderInboundsOrderListHeader = useMemo(
    () => (
      <View
        style={[
          styles.headerContainer,
          (drawerOpen && isSmallTabletLayout) || !isSmallMobile
            ? null
            : styles.headerContainerSmallMobile,
        ]}
      >
        <View style={styles.headerRow}>
          {drawerOpen ? (
            <>
              <TouchableOpacity
                hitSlop={ICON_BUTTON_HIT_SLOP}
                onPress={() => {
                  if (navigation.canGoBack()) {
                    setActiveSelection({
                      mergeUUID: null,
                      order: null,
                      rejectedUpdatedAt: null,
                      submittedOrderUUID: null,
                      threadIds: [],
                    });
                    closeDrawer();
                    navigation.goBack();
                  }
                }}
              >
                <ArrowLeft fill={theme.sk.color.icon.primary} />
              </TouchableOpacity>

              <View style={styles.headerActionsContainer}>
                <TouchableOpacity
                  hitSlop={ICON_BUTTON_HIT_SLOP}
                  onPress={() => refetchAll()}
                >
                  <RefreshIcon fill={theme.sk.color.icon.primary} />
                </TouchableOpacity>

                <TouchableOpacity
                  hitSlop={ICON_BUTTON_HIT_SLOP}
                  onPress={closeDrawer}
                >
                  <ChevronLeft fill={theme.sk.color.icon.primary} />
                </TouchableOpacity>
              </View>
            </>
          ) : null}
        </View>

        <View style={styles.headerTitleContainer}>
          <Typography variant="heading-xl">
            {i18n.t('order_inbounds')}
          </Typography>
        </View>
      </View>
    ),
    [
      styles.headerContainer,
      styles.headerContainerSmallMobile,
      styles.headerRow,
      styles.headerActionsContainer,
      styles.headerTitleContainer,
      drawerOpen,
      isSmallTabletLayout,
      theme.sk.color.icon.primary,
      closeDrawer,
      navigation,
      refetchAll,
      isSmallMobile,
    ],
  );

  const OrderInboundsOrderListStickyHeader = useMemo(
    () =>
      isInboundHistoryEnabled ? (
        <View style={styles.stickyHeaderContainer}>
          <OrderInboundsOrderListFilterForm
            onChange={(values) => {
              // Switching the status pill (Open/Closed/All) clears the
              // selected smart order so the detail view empties out rather
              // than lingering on (or auto-selecting) an order from the new
              // tab. Search submits share this handler but keep the same
              // filter, so they leave the selection alone.
              if (values.filter !== activeStatusFilter) {
                if (drawerOpen) {
                  closeDrawer();
                }
                onBack();
              }
              setActiveStatusFilter(values.filter);
              setQueryFilters({
                searchTerm: values.search,
                // "Closest to cutoff time" is disabled on the Closed tab
                // (a cutoff means nothing once an order is closed), so
                // landing there while it's applied drops back to the default.
                // Deliberately not restored on the way back to Open -- the
                // user re-picks it if they still want it.
                ...(values.filter === InboundListFilter.Closed &&
                appliedSortBy === SmartOrderInboundHistoryOrderBy.ClosestCutoff
                  ? { sortBy: SmartOrderInboundHistoryOrderBy.NewestFirst }
                  : {}),
              });
            }}
            statusCounts={{
              closed: inboundListCounts[InboundListFilter.Closed],
              open: inboundListCounts[InboundListFilter.Open],
            }}
          />
        </View>
      ) : null,
    [
      isInboundHistoryEnabled,
      styles.stickyHeaderContainer,
      activeStatusFilter,
      drawerOpen,
      closeDrawer,
      onBack,
      setActiveStatusFilter,
      setQueryFilters,
      appliedSortBy,
      inboundListCounts,
    ],
  );

  const orderInboundsOrderListProps: OrderInboundsOrderListProps = useMemo(
    () => ({
      activeSelection,
      collapseHeaderOnScroll: isSmallMobile,
      getRemoveHandler,
      hasLoadFailed,
      isFetchingNextPage,
      isSmallMobile,
      isViewOnly,
      ListHeaderComponent: OrderInboundsOrderListHeader,
      listRef,
      ListStickyHeaderComponent: OrderInboundsOrderListStickyHeader,
      navigateToOrderDetail,
      onEndReached: fetchNextPage,
      onRetry: retryActiveTab,
      ordersByCustomer,
      refetch: refetchAll,
      renderFullWidth,
      sectionData,
      setActiveCustomerId: handleSetActiveCustomerId,
      setActiveSelection,
    }),
    [
      activeSelection,
      fetchNextPage,
      handleSetActiveCustomerId,
      hasLoadFailed,
      isFetchingNextPage,
      isSmallMobile,
      isViewOnly,
      navigateToOrderDetail,
      ordersByCustomer,
      refetchAll,
      renderFullWidth,
      retryActiveTab,
      sectionData,
      OrderInboundsOrderListHeader,
      OrderInboundsOrderListStickyHeader,
    ],
  );

  useEffect(() => {
    setDrawerState({ orderInboundsOrderListProps });
  }, [setDrawerState, orderInboundsOrderListProps]);

  useEffect(() => {
    // For desktop: If the initial ID route param changes, we need to
    // be able to route to a new order.
    if (initialSmartOrderUUID !== prevInitialSmartOrderUUID.current) {
      prevInitialSmartOrderUUID.current = initialSmartOrderUUID;
      initialActiveOrderSet.current = false;
    }
  }, [initialSmartOrderUUID]);

  useEffect(() => {
    if (
      hasResponse &&
      !initialActiveOrderSet.current &&
      sectionData &&
      sectionData.length > 0
    ) {
      const firstRestaurant = sectionData[0];
      const firstRestaurantOrder = firstRestaurant?.data[0];
      let initialOrderFromParam: OrderInboundOption | null = null;

      for (const section of sectionData) {
        const foundSmartOrder = section.data.find((option) => {
          const allSmartOrdersUUIDs = option.metadata?.orderUUIDs ?? [];
          return allSmartOrdersUUIDs.some(
            (orderUUID) => orderUUID === initialSmartOrderUUID,
          );
        });
        if (foundSmartOrder) {
          initialOrderFromParam = foundSmartOrder;
          break;
        }
      }

      // only navigate to detail screen if param is passed on small layout
      if (renderFullWidth && initialOrderFromParam) {
        navigateToOrderDetail(initialOrderFromParam);
      } else if (!renderFullWidth) {
        const nextOrder = initialOrderFromParam || firstRestaurantOrder;
        if (nextOrder) {
          setActiveSelection({
            mergeUUID: nextOrder.metadata?.mergeUUID ?? null,
            order: nextOrder,
            rejectedUpdatedAt: nextOrder.metadata?.rejectedUpdatedAt ?? null,
            submittedOrderUUID: nextOrder.metadata?.submittedOrderUUID ?? null,
            threadIds: nextOrder.metadata?.threadIds ?? [],
          });
        }
      }
      initialActiveOrderSet.current = true;
    }
  }, [
    hasResponse,
    sectionData,
    initialSmartOrderUUID,
    renderFullWidth,
    navigateToOrderDetail,
  ]);

  const getOrderInboundsList = useCallback(
    () => (
      <>
        <StatusBar
          backgroundColor={theme.colors.white}
          barStyle="dark-content"
        />

        <OrderInboundsOrderList {...orderInboundsOrderListProps} />
      </>
    ),
    [theme, orderInboundsOrderListProps],
  );

  const toggleInboundsListPanel = (isExpanded: boolean) => {
    setNextLayoutAnimation();
    setIsInboundsListExpanded(isExpanded);
  };

  const removeInboundActionSheet =
    ordersToRemove && ordersToRemove.length > 0 ? (
      <RemoveOrderInboundActionSheet
        onBeforeRemove={() => {
          for (const orderUUID of ordersToRemove) {
            addRemovedOrderUUID(orderUUID);
          }
        }}
        onClose={() => setOrdersToRemove(null)}
        onRemove={() => {
          // Only reselect the next order if the active order is getting removed
          const activeOrderUUID = String(activeSelection.order?.value ?? '');
          if (ordersToRemove.includes(activeOrderUUID)) {
            selectNextSmartOrder(activeOrderUUID);
          }
        }}
        smartOrderUUIDs={ordersToRemove}
      />
    ) : null;

  const mergeInboundsActionSheet =
    activeCustomerId && ordersByCustomer[activeCustomerId] ? (
      <MergeOrderInboundsActionSheet
        initialSelectedOrderUUID={initialMergeOrderUUID ?? undefined}
        onClose={() => {
          setActiveCustomerId(null);
          setInitialMergeOrderUUID(null);
        }}
        onMerge={(mergedOrders, newMergeUUID) => {
          selectSmartOrderByIds(mergedOrders, newMergeUUID);
        }}
        orderInboundSection={ordersByCustomer[activeCustomerId]}
      />
    ) : null;

  return (
    <>
      {mergeInboundsActionSheet}
      {removeInboundActionSheet}
      {renderFullWidth ? (
        <PageContainer
          animateNavigationHeader
          containerStyle={{ paddingTop }}
          navigationHeaderProps={{
            actions: isSmallLayout
              ? [
                  {
                    component: <TrainingModeSwitch />,
                  },
                ]
              : [
                  {
                    component: (
                      <TouchableOpacity
                        hitSlop={ICON_BUTTON_HIT_SLOP}
                        onPress={() => refetchAll()}
                      >
                        <RefreshIcon fill={theme.sk.color.icon.primary} />
                      </TouchableOpacity>
                    ),
                  },
                ],
            onBackPress: onNavigateToAppStack,
            showBackButton: true,
            showShadow: false,
          }}
          showNavigationHeader
        >
          {getOrderInboundsList}
        </PageContainer>
      ) : (
        <InboundListSplitPageContainer
          actions={[
            {
              Icon: RefreshIcon,
              iconProps: {
                fill: theme.sk.color.icon.tertiary,
                height: 16,
                testID: 'refresh-inbounds-list',
                width: 16,
              },
              onPress: () => refetchAll(),
            },
            ...((isMediumLayout || isLargeLayout) &&
            !loading &&
            activeSelection.order
              ? [
                  {
                    Icon: ChevronLeft,
                    iconProps: {
                      fill: theme.sk.color.icon.tertiary,
                      height: 16,
                      testID: 'collapse-inbounds-list-panel',
                      width: 16,
                    },
                    onPress: () => toggleInboundsListPanel(false),
                  },
                ]
              : []),
          ]}
          isPrimaryPanelOpen={isInboundsListExpanded}
          navigationHeaderProps={{
            onBackPress: onNavigateToAppStack,
            showBackButton: true,
          }}
          primaryComponent={getOrderInboundsList()}
          skipPanelSpacing={isLargeLayout}
        >
          {activeSelection.order ? (
            <OrderInboundWrapper
              initialMergeUUID={
                activeSelection.mergeUUID
                  ? String(activeSelection.mergeUUID)
                  : undefined
              }
              initialProcessing={
                activeSelection.order.metadata?.isProcessing ?? false
              }
              initialRejectedUpdatedAt={
                isEmailThreadsEnabled
                  ? (activeSelection.rejectedUpdatedAt ?? undefined)
                  : undefined
              }
              initialRestaurantUUID={
                activeSelection.order.metadata?.restaurantUUID ?? null
              }
              initialSubmittedOrderUUID={
                isEmailThreadsEnabled
                  ? (activeSelection.submittedOrderUUID ?? undefined)
                  : undefined
              }
              initialThreadIds={
                isEmailThreadsEnabled ? activeSelection.threadIds : undefined
              }
              isInboundsListExpanded={isInboundsListExpanded}
              key={activeInboundKey ?? undefined}
              onAssignSmartOrder={(
                orderUUID: string,
                restaurantUUID: string,
              ) => {
                // Flags this order to be scrolled to once `sectionData` is updated
                scrollData.current = {
                  orderUUID,
                  restaurantUUID,
                };
              }}
              onClose={onBack}
              onRegenerateSmartOrder={(
                orderUUID: string,
                restaurantUUID: string,
              ) => {
                // Flags this order to be scrolled to once `sectionData` is updated
                scrollData.current = {
                  orderUUID,
                  restaurantUUID,
                };
              }}
              orderUUID={String(activeSelection.order.value)}
              selectNextSmartOrder={selectNextSmartOrder}
              setIsInboundsListExpanded={toggleInboundsListPanel}
              unknownContactInfo={
                activeSelection.order.metadata?.unknownContactInfo
              }
            />
          ) : null}
        </InboundListSplitPageContainer>
      )}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  headerActionsContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 18,
    marginLeft: 'auto',
  },
  headerContainer: {
    paddingHorizontal: PAGE_CONTAINER_PADDING_HORIZONTAL,
    paddingVertical: 8,
  },
  headerContainerSmallMobile: {
    marginTop: PAGE_CONTAINER_HEADER_HEIGHT,
  },
  headerRow: {
    flexDirection: 'row',
  },
  headerTitleContainer: {
    marginTop: 16,
  },
  stickyHeaderContainer: {
    backgroundColor: theme.colors.white,
    paddingBottom: 8,
    paddingHorizontal: PAGE_CONTAINER_PADDING_HORIZONTAL,
    paddingTop: 8,
  },
}));
